import { forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { variantStyles, getDurationBarClass } from './toastVariants';
import { getAnimationVariants } from './toastStyles';
import type { ToastPosition, ToastProps } from './toastTypes';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Individual Toast component
 */
export const ToastCard = forwardRef<HTMLDivElement, ToastProps & { position: ToastPosition }>(
  ({ toast, onDismiss, position, className, ...props }, ref) => {
    const { t } = useTranslation();
    const { icon, colors } = variantStyles[toast.variant];
    const animationVariants = getAnimationVariants(position);

    // Auto dismiss
    useEffect(() => {
      if (toast.duration && toast.duration > 0) {
        const timer = setTimeout(onDismiss, toast.duration);
        return () => clearTimeout(timer);
      }
    }, [toast.duration, onDismiss]);

    return (
      <motion.div
        ref={ref}
        layout
        variants={animationVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className={cn(
          'relative w-80 max-w-full',
          'backdrop-blur-xl',
          'bg-surface-100/90',
          'border border-white/[0.12]',
          'rounded-xl',
          'shadow-2xl shadow-black/40',
          'overflow-hidden',
          className
        )}
        role="alert"
        aria-live="polite"
        {...(props as Omit<HTMLMotionProps<'div'>, 'ref'>)}
      >
        {/* Progress bar */}
        {toast.duration && toast.duration > 0 && (
          <motion.div
            className={cn('absolute top-0 left-0 h-0.5', getDurationBarClass(colors))}
            initial={{ width: '100%' }}
            animate={{ width: '0%' }}
            transition={{ duration: toast.duration / 1000, ease: 'linear' }}
          />
        )}

        <div className="flex items-start gap-3 p-4">
          {/* Icon */}
          <div className={cn('flex-shrink-0 p-1 rounded-lg', colors)}>{icon}</div>

          {/* Content */}
          <div className="flex-1 min-w-0 pt-0.5">
            <p className="text-sm font-semibold text-surface-900">{toast.title}</p>
            {toast.description && (
              <p className="mt-1 text-sm text-surface-500">{toast.description}</p>
            )}
            {toast.action && (
              <button
                type="button"
                onClick={toast.action.onClick}
                className={cn(
                  'mt-2 text-sm font-medium',
                  'text-primary hover:text-primary',
                  'transition-colors duration-150'
                )}
              >
                {toast.action.label}
              </button>
            )}
          </div>

          {/* Close button */}
          <button
            type="button"
            onClick={onDismiss}
            className={cn(
              'flex-shrink-0 p-1 rounded-lg',
              'text-surface-500 hover:text-surface-800',
              'hover:bg-white/[0.08]',
              'transition-colors duration-150',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20'
            )}
            aria-label={t('a11y.dismiss')}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </motion.div>
    );
  }
);

// Der Anzeigename bleibt bewusst 'Toast': Er ist in React DevTools und in
// Fehlermeldungen sichtbar, eine Umbenennung waere eine Verhaltensaenderung.
ToastCard.displayName = 'Toast';
